import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { cn } from "@/lib/utils";
import { Plug, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { fmtTime } from "./shared";
import { useAtlas } from "@/context/AtlasContext";

const STATUS = { healthy: { c: "text-teal-700 bg-teal-50 border-teal-300", i: CheckCircle2 }, degraded: { c: "text-amber-700 bg-amber-50 border-amber-300", i: AlertTriangle }, disabled: { c: "text-slate-500 bg-slate-50 border-slate-200", i: XCircle } };

export default function PulseSources() {
  const { has } = useAtlas();
  const [sources, setSources] = useState([]);
  useEffect(() => { api.get("/pulse/sources").then((r) => setSources(r.data)).catch(() => {}); }, []);
  if (!has("pulse.view")) return <div className="max-w-3xl mx-auto panel p-10 text-center text-sm text-slate-500" data-testid="pulse-forbidden">You don't have access to Pulse in this workspace.</div>;
  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="pulse-sources">
      <PageHeader title="Source Health" subtitle="Coverage and freshness for every Pulse signal source. Unavailable data is never silently counted as zero activity." />
      <div className="space-y-3">{sources.map((s) => {
        const st = STATUS[s.status] || STATUS.disabled; const Icon = st.i;
        return (<div key={s.key} className="panel p-4" data-testid={`source-${s.key}`}>
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><Plug className="w-4 h-4 text-slate-500" /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap"><span className="font-heading font-semibold">{s.name}</span>
                <span className={cn("chip text-[11px]", st.c)}><Icon className="w-3 h-3" />{s.status}</span>
                {s.demo && <span className="chip border-slate-200 text-slate-400 text-[10px]">seeded demo</span>}
                {s.kind === "native" && <span className="chip border-teal-200 text-teal-700 text-[10px]">native</span>}
              </div>
              {s.detail && <p className="text-xs text-amber-700 mt-1">{s.detail}</p>}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                <Kv k="Coverage" v={`${s.coverage}%`} />
                <Kv k="Sync lag" v={`${s.sync_lag_hours ?? 0}h`} />
                <Kv k="Identities" v={s.identities_mapped ?? "—"} />
                <Kv k="Unresolved" v={s.unresolved_identities ?? 0} />
                <Kv k="Last sync" v={fmtTime(s.last_sync)} />
              </div>
            </div>
            <div className="text-right"><div className="font-heading text-xl font-semibold tabular" style={{ color: s.coverage >= 90 ? "#0f766e" : s.coverage >= 75 ? "#d97706" : "#be123c" }}>{s.coverage}%</div><div className="text-[10px] text-slate-400">coverage</div></div>
          </div>
        </div>);
      })}</div>
    </div>);
}

function Kv({ k, v }) { return (<div><span className="text-slate-400">{k}: </span><span className="text-slate-700 tabular">{v}</span></div>); }
